import * as React from 'react';
import { useEffect, useState } from 'react';
import * as alpha from '@journeyapps-platform/types-alpha';
import { SourceType } from '@journeyapps-platform/types-source-service';
import { MigrateAppStep1Widget } from './MigrateAppStep1Widget';
import { JourneyNavigationHandler, Routes } from '../../../routes';
import { MigrateAppStep2Widget } from './MigrateAppStep2Widget';
import { useSystem } from '../../../hooks/useSystem';
import { Region } from '../../../utils/environments';
import { MigrateAppStep3Widget } from './MigrateAppStep3Widget';
import { useOrgStore } from '../../../hooks/useOrgStore';
import { observer } from 'mobx-react';
import { VCS_MODE } from '@journeyapps-platform/types-accounts-hub';
import { useDialogStore } from '../../../hooks/useDialogService';
import { useAllowed } from '../../../hooks/useAllowed';
import { getAppUrl } from '@journeyapps-platform/types-accounts';
import { ENV } from '../../../env';
import { AppModelAction } from '@journeyapps-platform/cardinal-catalog';

export enum MigrateStep {
  STEP_INFO = 'step1',
  STEP_MIGRATE = 'step2',
  STEP_CONFIRM = 'step3'
}

export const VersionControl = {
  [VCS_MODE.BASIC]: 'Basic (Revisions)',
  [VCS_MODE.ADVANCED]: 'Advanced (Git)'
};

export const GitProvider = {
  [SourceType.Internal]: 'JourneyApps',
  [SourceType.GitHub]: 'GitHub',
  [SourceType.AzureDevops]: 'Azure Repos'
};

export type MigrateAppPayload = Omit<alpha.MigrateAppParams, 'region'> & {
  token?: string;
  step: MigrateStep;
  region: Region;
  loading?: boolean;
  original_app_id?: string;
};

export const MigrateAppPage: React.FC<{ handler: JourneyNavigationHandler<Routes.MIGRATE_APP> }> = observer((props) => {
  const system = useSystem();
  const orgStore = useOrgStore();
  const dialogStore = useDialogStore();
  const { navigate, params } = props.handler;

  useEffect(() => {
    (async () => {
      const org = await system.orgStore.resolve(params.org_id);
      await org.getApp(params.app_id);
    })();
  }, []);

  // check for app
  const org = orgStore.org(params.org_id);
  const app = org?.app(params?.app_id);

  const isAllowed = useAllowed({ action: AppModelAction.CREATE, resource: app ?? null });

  const [payload, setPayload] = useState<MigrateAppPayload>({
    app_id: params.app_id,
    step: MigrateStep.STEP_INFO,
    vcs_mode: VCS_MODE.BASIC,
    source: null as any,
    region: null as any
  });

  const showErrorDialog = async () => {
    return dialogStore.errorDialog({
      subTitle: 'Failed to migrate app',
      errorMessage: `Oh no, something went wrong and this app couldn't be migrated. Please try again in a few minutes.`
    });
  };

  const migrateApp = async () => {
    if (!isAllowed) {
      await dialogStore.errorDialog({
        title: 'Not Allowed',
        errorMessage: `You do not have permission to migrate a app. Ask your administrator to migrate this app, or to elevate your permissions.`
      });
      return;
    }

    const alpha = system.clientProvisioner.provisionAlphaClient();

    const commonParams = {
      source: payload.source,
      token: payload.token,
      vcs_mode: payload.vcs_mode
    };

    try {
      const res = await alpha.migrateApp({
        ...commonParams,
        app_id: payload.app_id
      });

      if (!res) {
        console.error('Empty app returned in response: ', res);
        await showErrorDialog();
        return;
      }
      navigate(Routes.MANAGE_APPS, params);

      const app = await system.accountV5Client.getApp({ id: params.app_id });

      window.location = getAppUrl({
        app,
        env: ENV
      }) as any;
    } catch (err) {
      console.error('Failed to create app: ', err);
      await showErrorDialog();
    }
  };

  if (!app) {
    return null;
  }

  if (payload.step === MigrateStep.STEP_INFO) {
    return (
      <MigrateAppStep1Widget
        name={`${app?.model.name}`}
        payload={payload}
        onCancel={() => {
          navigate(Routes.MANAGE_APPS, params);
        }}
        next={(p) => {
          setPayload({
            ...payload,
            ...p,
            step: MigrateStep.STEP_MIGRATE
          });
        }}
      />
    );
  }

  if (payload.step === MigrateStep.STEP_MIGRATE) {
    return (
      <MigrateAppStep2Widget
        org={org}
        handler={props.handler}
        name={`${app?.model.name}`}
        payload={payload}
        onCancel={() => {
          navigate(Routes.MANAGE_APPS, params);
        }}
        back={() => {
          setPayload({ ...payload, step: MigrateStep.STEP_INFO });
        }}
        updatePayload={(p, next = false) => {
          setPayload({
            ...payload,
            ...p,
            step: next ? MigrateStep.STEP_CONFIRM : MigrateStep.STEP_MIGRATE
          });
        }}
        next={() => {
          setPayload({
            ...payload,
            step: MigrateStep.STEP_CONFIRM
          });
        }}
      />
    );
  }
  if (payload.step === MigrateStep.STEP_CONFIRM) {
    return (
      <MigrateAppStep3Widget
        payload={payload}
        onCancel={() => {
          navigate(Routes.MANAGE_APPS, params);
        }}
        back={() => {
          setPayload({ ...payload, step: MigrateStep.STEP_MIGRATE });
        }}
        updatePayload={async (p) => {
          await new Promise<void>((resolve) => {
            setPayload({
              ...payload,
              ...p
            });
            resolve();
          });
        }}
        next={async () => {
          setPayload({
            ...payload,
            loading: true
          });
          await migrateApp();
          setPayload({
            ...payload,
            loading: false
          });
        }}
      />
    );
  }
  return <div>Empty</div>;
});
